import {useNavigate, useParams} from 'react-router-dom';
import {useFetch} from '../../../hooks/useFetch';
import CounterCall from './CounterCall';
import { getServerNow } from '../../../hooks/useServerTime';
import LoadingContent, {ErrorContent} from '../../../components/LoadingContent';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Rating,
    Stack,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import OutIcon from '@mui/icons-material/ExitToApp';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
    addHours,
    isAfter,
    isBefore,
    isFuture,
    isPast,
    isWithinInterval,
    parseISO,
    subHours,
    subMinutes,
    subSeconds,
} from 'date-fns';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSnackbar} from 'notistack';
import useHandleErrors from '../../../hooks/useHandleErrors';
import server from '../../../global/server';

export default ({inCall, setInCall}) => {
    const params = useParams();

    // Apagar a sala no LiveKit nem sempre tira o conviva da chamada: o cliente
    // reconecta sozinho e recria a sala. Quem garante a saida e esta consulta
    // periodica ao estado do encontro.
    const {data, mutate, error, isLoading} = useFetch(`booking/${params.id}`, undefined, false, {
        refreshInterval: 5000,
    });
    useHandleErrors(error);

    const [finishError, setFinishError] = useState();
    useHandleErrors(finishError);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isFinishing, setFinishing] = useState(false);

    const {enqueueSnackbar} = useSnackbar();
    const navigation = useNavigate();

    const isValid = useCallback(() => {
        const now = getServerNow();
        const meetingStart = parseISO(data.datetime);
        const diffSeconds = (now.getTime() - meetingStart.getTime()) / 1000;
        // Valido: desde 5 segundos antes ate 1 hora (3600s) depois, e enquanto
        // o encontro nao tiver sido finalizado
        const valid = diffSeconds >= -5 && diffSeconds <= 3600 && !data.finished;
        setInCall(valid);
        mutate();
    }, [data]);

    useEffect(() => {
        if (data) {
            isValid(data);
        }
    }, [data]);

    // Quem finalizou ja sai com a propria mensagem de sucesso
    const finishedByMe = useRef(false);
    const leaving = useRef(false);

    // Vale para os dois lados: encerrado pelo anfitriao ou por tempo, ninguem
    // pode continuar na sala
    useEffect(() => {
        if (!data?.finished || finishedByMe.current || leaving.current) return;

        leaving.current = true;
        setInCall(false);
        enqueueSnackbar('O encontro foi finalizado.', {variant: 'info'});
        navigation(`/encontros/${params.id}/detalhes`);
    }, [data?.finished]);

    // Saida do conviva (ou do anfitriao depois de finalizado). Nao usamos
    // navigation(-1) porque o link da chamada costuma ser aberto direto do
    // WhatsApp: sem historico anterior, o "voltar" nao levava a lugar nenhum e
    // o usuario ficava preso na sala.
    const leaveCall = () => {
        leaving.current = true;
        setInCall(false);
        navigation(`/encontros/${params.id}/detalhes`);
    };

    // Fecha o encontro para todo mundo: o servidor marca como finalizado e
    // derruba a sala, entao o conviva sai junto
    const finishMeeting = async () => {
        try {
            setFinishing(true);
            const {data: response} = await server.post(`/booking/${params.id}/finish`);

            finishedByMe.current = true;
            setInCall(false);
            setConfirmOpen(false);
            enqueueSnackbar(response.message, {variant: 'success'});
            navigation(`/encontros/${params.id}/detalhes`);
        } catch (err) {
            console.log(err);
            setConfirmOpen(false);
            setFinishError(err);
        } finally {
            setFinishing(false);
        }
    };

    if (isLoading) return <LoadingContent />;
    if (error) return <ErrorContent label="Ocorreu um erro. Volte mais tarde!" />;

    const now = getServerNow();
    const meetingStart = parseISO(data.datetime);
    const diffSec = (now.getTime() - meetingStart.getTime()) / 1000;
    const past = diffSec < -5;       // Encontro ainda nao comecou
    const future = diffSec > 3600;    // Passou de 1 hora
    const finished = Boolean(data.finished);
    const isHost = Boolean(data.isHost);

    return (
        <>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={2}
                sx={{mb: 2}}>
                <CounterCall datetime={data.datetime} mutate={e => isValid()} />
                {/* <Typography sx={{color: '#fff'}}>{observers.length} espectadores</Typography> */}
                {isHost && !finished ? (
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<StopCircleIcon />}
                        onClick={e => setConfirmOpen(true)}>
                        Finalizar encontro
                    </Button>
                ) : (
                    <Button variant="contained" startIcon={<OutIcon />} onClick={leaveCall}>
                        Sair da chamada
                    </Button>
                )}
            </Stack>

            {finished && !inCall && (
                <Stack justifyContent="center" alignItems="center" height="100%">
                    <Typography sx={{color: '#fff'}}>Este encontro foi finalizado.</Typography>
                </Stack>
            )}
            {future && !finished && !inCall && (
                <Stack justifyContent="center" alignItems="center" height="100%">
                    <Typography sx={{color: '#fff'}}>O tempo do encontro foi encerrado.</Typography>
                </Stack>
            )}
            {past && !finished && !inCall && (
                <Stack justifyContent="center" alignItems="center" height="100%">
                    <Typography sx={{color: '#fff'}}>Aguardando o horário do encontro</Typography>
                </Stack>
            )}

            <Dialog open={confirmOpen} onClose={() => !isFinishing && setConfirmOpen(false)}>
                <DialogTitle>Finalizar encontro?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        O encontro será marcado como finalizado e a chamada será encerrada para você e
                        para o conviva. Não e possível voltar para esta sala depois.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={isFinishing}>
                        Cancelar
                    </Button>
                    <LoadingButton
                        onClick={finishMeeting}
                        color="error"
                        variant="contained"
                        loading={isFinishing}>
                        Finalizar encontro
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
